import { TypeCompiler } from '@sinclair/typebox/compiler'
import { isNil } from '../../common'
import { ActivepiecesError, ErrorCode } from '../../common/activepieces-error'
import {
  Action,
  ActionType,
  LoopOnItemsAction,
  RouterAction,
  SingleActionSchema,
} from '../actions/action'
import { Connection } from '../connections/connection'
import { FlowVersion } from '../flow-version'
import { flowStructureUtil } from '../util/flow-structure-util'
import {
  AddActionRequest,
  StepLocationRelativeToParent,
  UpdateActionRequest,
} from './index'

const actionSchemaValidator = TypeCompiler.Compile(SingleActionSchema)

type ActionCreationProps = {
  nextAction?: Action
}

function calculatePositionBetweenSteps(
  sourceStep: { settings: { position: { x: number; y: number } } },
  targetStep: { settings: { position: { x: number; y: number } } }
): { x: number; y: number } {
  return {
    x: (sourceStep.settings.position.x + targetStep.settings.position.x) / 2,
    y: (sourceStep.settings.position.y + targetStep.settings.position.y) / 2,
  }
}

function determineActionPosition(
  request: AddActionRequest,
  flowVersion: FlowVersion
): { x: number; y: number } | undefined {
  if (request.newStepPosition) {
    return request.newStepPosition
  }

  if (request.connection?.sourceId && request.connection.targetId) {
    const sourceStep = flowStructureUtil.getStep(
      request.connection.sourceId,
      flowVersion
    )
    const targetStep = flowStructureUtil.getStep(
      request.connection.targetId,
      flowVersion
    )
    if (sourceStep && targetStep) {
      return calculatePositionBetweenSteps(sourceStep, targetStep)
    }
  }

  return undefined
}

function createAction(
  request: UpdateActionRequest,
  { nextAction }: ActionCreationProps
): Action {
  const baseProperties = {
    displayName: request.displayName,
    name: request.name,
    valid: false,
    skip: request.skip,
    customLogoUrl: request.customLogoUrl,
    nextAction,
  }
  let action: Action
  switch (request.type) {
    case ActionType.ROUTER:
      action = {
        ...baseProperties,
        type: ActionType.ROUTER,
        settings: request.settings,
        children: request.settings.branches.map(() => null),
      }

      break
    case ActionType.LOOP_ON_ITEMS:
      action = {
        ...baseProperties,
        type: ActionType.LOOP_ON_ITEMS,
        settings: request.settings,
      }
      break
    case ActionType.PIECE:
      action = {
        ...baseProperties,
        type: ActionType.PIECE,
        settings: request.settings,
      }
      break
    case ActionType.CODE:
      action = {
        ...baseProperties,
        type: ActionType.CODE,
        settings: request.settings,
      }
      break
  }
  const valid =
    (isNil(request.valid) ? true : request.valid) &&
    actionSchemaValidator.Check(action)
  return {
    ...action,
    valid,
  }
}

function _addAction(
  flowVersion: FlowVersion,
  request: AddActionRequest
): FlowVersion {
  let updatedFlowVersion: FlowVersion = structuredClone(flowVersion)
  const loopParentStep = flowStructureUtil.getParentLoopStepName(
    updatedFlowVersion,
    request.parentStep[0]
  )
  const routerParentResult =
    flowStructureUtil.getParentRouterStepNameAndBranchIndex(
      updatedFlowVersion,
      request.parentStep[0]
    )
  const routerParentName = routerParentResult?.routerParentName
  const branchIndex = routerParentResult?.branchIndex ?? null

  switch (request.stepLocationRelativeToParent) {
    case StepLocationRelativeToParent.INSIDE_LOOP:
      updatedFlowVersion = handleFirstLoopConnections(
        updatedFlowVersion,
        request
      )
      break
    case StepLocationRelativeToParent.INSIDE_BRANCH:
      updatedFlowVersion = handleFirstBranchConnections(
        updatedFlowVersion,
        request
      )
      break
    default:
      if (loopParentStep) {
        updatedFlowVersion = request.connection
          ? handleInsideLoopConnections(
              updatedFlowVersion,
              request,
              loopParentStep
            )
          : handleCloseLoopConnections(
              updatedFlowVersion,
              request,
              loopParentStep
            )
      } else if (routerParentName && branchIndex !== null) {
        updatedFlowVersion = handleInsideBranchConnections(
          updatedFlowVersion,
          request,
          routerParentName,
          branchIndex
        )
      } else {
        updatedFlowVersion = handleNormalConnections(
          updatedFlowVersion,
          request
        )
      }
      break
  }

  return updatedFlowVersion
}

function handleFirstBranchConnections(
  updatedFlowVersion: FlowVersion,
  request: AddActionRequest
): FlowVersion {
  if (request.newStepPosition) {
    request.action.settings.position = request.newStepPosition
  }
  const branchStep = flowStructureUtil.getStepOrThrow(
    request.parentStep[0],
    updatedFlowVersion
  ) as RouterAction
  const branchIndex = request.branchIndex

  if (!isNil(branchIndex)) {
    const branchSubflow = branchStep.children[branchIndex] || {
      actions: [],
      connections: [],
    }

    if (branchSubflow.actions.length === 0) {
      branchSubflow.actions.push(
        createAction(request.action, { nextAction: undefined })
      )
      branchSubflow.connections.push({
        sourceId: branchStep.name,
        targetId: request.action.name,
      })
    } else {
      const firstActionName = flowStructureUtil.getBranchFirstStepName(
        updatedFlowVersion,
        branchStep.name,
        branchIndex
      )
      if (firstActionName && !request.newStepPosition) {
        const firstAction = flowStructureUtil.getStepOrThrow(
          firstActionName,
          updatedFlowVersion
        )
        request.action.settings.position = {
          x: firstAction.settings.position.x - 200,
          y: firstAction.settings.position.y,
        }
      }

      const newAction = createAction(request.action, { nextAction: undefined })
      branchSubflow.actions.push(newAction)

      const oldConnectionIndex = branchSubflow.connections.findIndex(
        (connection: Connection) => connection.sourceId === branchStep.name
      )

      if (oldConnectionIndex !== -1) {
        const oldConnection = branchSubflow.connections[oldConnectionIndex]
        branchSubflow.connections.push({
          sourceId: newAction.name,
          targetId: oldConnection.targetId,
        })
        branchSubflow.connections.splice(oldConnectionIndex, 1)
      }

      branchSubflow.connections.push({
        sourceId: branchStep.name,
        targetId: newAction.name,
      })
    }

    branchStep.children[branchIndex] = branchSubflow
  }

  return updatedFlowVersion
}

function handleInsideBranchConnections(
  updatedFlowVersion: FlowVersion,
  request: AddActionRequest,
  routerParentStep: string,
  branchIndex: number
): FlowVersion {
  const router = flowStructureUtil.getStepOrThrow(
    routerParentStep,
    updatedFlowVersion
  ) as RouterAction
  const branchSubflow = router.children[branchIndex]

  if (!branchSubflow) {
    throw new ActivepiecesError({
      code: ErrorCode.FLOW_OPERATION_INVALID,
      params: {
        message: `Branch ${branchIndex} does not exist in router ${routerParentStep}.`,
      },
    })
  }

  const position = determineActionPosition(request, updatedFlowVersion)
  if (position) {
    request.action.settings.position = position
  }

  const newAction = createAction(request.action, { nextAction: undefined })
  branchSubflow.actions.push(newAction)

  if (
    request.connection &&
    request.connection.sourceId &&
    request.connection.targetId
  ) {
    const connectionIndex = branchSubflow.connections.findIndex(
      (conn: Connection) =>
        request.connection &&
        conn.sourceId === request.connection.sourceId &&
        conn.targetId === request.connection.targetId
    )

    if (connectionIndex !== -1) {
      branchSubflow.connections.splice(connectionIndex, 1)
      branchSubflow.connections.push({
        sourceId: request.connection.sourceId,
        targetId: newAction.name,
      })
      branchSubflow.connections.push({
        sourceId: newAction.name,
        targetId: request.connection.targetId,
      })
    }
  } else {
    branchSubflow.connections.push({
      sourceId: request.parentStep[0],
      targetId: newAction.name,
    })
  }

  return updatedFlowVersion
}

function handleNormalConnections(
  updatedFlowVersion: FlowVersion,
  request: AddActionRequest
): FlowVersion {
  const position = determineActionPosition(request, updatedFlowVersion)
  if (position) {
    request.action.settings.position = position
  }

  updatedFlowVersion.actions.push(
    createAction(request.action, { nextAction: undefined })
  )

  const parentSteps = Array.isArray(request.parentStep)
    ? request.parentStep
    : [request.parentStep]

  parentSteps.forEach((parentStep) => {
    if (parentStep) {
      updatedFlowVersion.connections.push({
        sourceId: parentStep,
        targetId: request.action.name,
      })
    }
  })

  if (
    request.connection &&
    request.connection.sourceId &&
    request.connection.targetId
  ) {
    updatedFlowVersion.connections.push({
      sourceId: request.action.name,
      targetId: request.connection.targetId,
    })

    const connectionToDeleteIndex = updatedFlowVersion.connections.findIndex(
      (connection: Connection) =>
        request.connection &&
        connection.sourceId === request.connection.sourceId &&
        connection.targetId === request.connection.targetId
    )
    updatedFlowVersion.connections.splice(connectionToDeleteIndex, 1)
  }

  return updatedFlowVersion
}

function handleFirstLoopConnections(
  updatedFlowVersion: FlowVersion,
  request: AddActionRequest
): FlowVersion {
  if (request.newStepPosition) {
    request.action.settings.position = request.newStepPosition
  }

  const loopStep = flowStructureUtil.getStepOrThrow(
    request.parentStep[0],
    updatedFlowVersion
  ) as LoopOnItemsAction

  if (!loopStep.subflow) {
    loopStep.subflow = { actions: [], connections: [] }
  }

  if (loopStep.subflow.actions.length === 0) {
    loopStep.subflow.actions.push(
      createAction(request.action, { nextAction: undefined })
    )
    loopStep.subflow.connections.push(
      { sourceId: loopStep.name, targetId: request.action.name },
      { sourceId: request.action.name, targetId: loopStep.name }
    )
  } else {
    const firstLoopActionName = flowStructureUtil.getLoopFirstStepName(
      updatedFlowVersion,
      loopStep.name
    )
    if (firstLoopActionName && !request.newStepPosition) {
      const firstLoopAction = flowStructureUtil.getStepOrThrow(
        firstLoopActionName,
        updatedFlowVersion
      )
      request.action.settings.position = {
        x: firstLoopAction.settings.position.x - 200,
        y: firstLoopAction.settings.position.y,
      }
    }

    const newAction = createAction(request.action, { nextAction: undefined })
    loopStep.subflow.actions.push(newAction)

    const oldConnectionIndex = loopStep.subflow.connections.findIndex(
      (connection: Connection) => connection.sourceId === loopStep.name
    )

    if (oldConnectionIndex !== -1) {
      const oldConnection = loopStep.subflow.connections[oldConnectionIndex]
      loopStep.subflow.connections.push({
        sourceId: newAction.name,
        targetId: oldConnection.targetId,
      })
      loopStep.subflow.connections.splice(oldConnectionIndex, 1)
    }

    loopStep.subflow.connections.push({
      sourceId: loopStep.name,
      targetId: newAction.name,
    })
  }

  return updatedFlowVersion
}

function handleCloseLoopConnections(
  updatedFlowVersion: FlowVersion,
  request: AddActionRequest,
  loopName: string
): FlowVersion {
  const loopStep = flowStructureUtil.getStepOrThrow(
    loopName,
    updatedFlowVersion
  ) as LoopOnItemsAction

  if (!loopStep.subflow) {
    throw new ActivepiecesError({
      code: ErrorCode.FLOW_OPERATION_INVALID,
      params: { message: `The loop '${loopName}' doesn't have a subflow.` },
    })
  }

  const lastLoopActionName = flowStructureUtil.getLoopLastStepName(
    updatedFlowVersion,
    loopStep.name
  )
  if (lastLoopActionName) {
    const lastLoopAction = flowStructureUtil.getStepOrThrow(
      lastLoopActionName,
      updatedFlowVersion
    )
    request.action.settings.position = {
      x: lastLoopAction.settings.position.x + 200,
      y: lastLoopAction.settings.position.y,
    }
  }

  const newAction = createAction(request.action, { nextAction: undefined })
  loopStep.subflow.actions.push(newAction)

  const oldConnectionIndex = loopStep.subflow.connections.findIndex(
    (connection: Connection) => connection.targetId === loopStep.name
  )

  if (oldConnectionIndex !== -1) {
    const oldConnection = loopStep.subflow.connections[oldConnectionIndex]
    loopStep.subflow.connections.push({
      sourceId: oldConnection.sourceId,
      targetId: request.action.name,
    })
    loopStep.subflow.connections.splice(oldConnectionIndex, 1)
  }

  loopStep.subflow.connections.push({
    sourceId: request.action.name,
    targetId: loopStep.name,
  })

  return updatedFlowVersion
}

function handleInsideLoopConnections(
  updatedFlowVersion: FlowVersion,
  request: AddActionRequest,
  loopName: string
): FlowVersion {
  const loopStep = flowStructureUtil.getStepOrThrow(
    loopName,
    updatedFlowVersion
  ) as LoopOnItemsAction

  if (!loopStep.subflow) {
    throw new ActivepiecesError({
      code: ErrorCode.FLOW_OPERATION_INVALID,
      params: { message: `The loop '${loopName}' doesn't have a subflow.` },
    })
  }

  const position = determineActionPosition(request, updatedFlowVersion)
  if (position) {
    request.action.settings.position = position
  }

  const newAction = createAction(request.action, { nextAction: undefined })
  loopStep.subflow.actions.push(newAction)

  if (
    request.connection &&
    request.connection.sourceId &&
    request.connection.targetId
  ) {
    const connectionIndex = loopStep.subflow.connections.findIndex(
      (conn: Connection) =>
        request.connection &&
        conn.sourceId === request.connection.sourceId &&
        conn.targetId === request.connection.targetId
    )

    if (connectionIndex === -1) {
      throw new ActivepiecesError({
        code: ErrorCode.FLOW_OPERATION_INVALID,
        params: {
          message: `Connection from ${request.connection.sourceId} to ${request.connection.targetId} not found in loop subflow.`,
        },
      })
    }

    loopStep.subflow.connections.splice(connectionIndex, 1)
    loopStep.subflow.connections.push(
      { sourceId: request.connection.sourceId, targetId: newAction.name },
      { sourceId: newAction.name, targetId: request.connection.targetId }
    )
  } else {
    throw new ActivepiecesError({
      code: ErrorCode.FLOW_OPERATION_INVALID,
      params: {
        message: 'Invalid connection data for insertion inside a loop.',
      },
    })
  }

  return updatedFlowVersion
}

export { _addAction }
